import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useOfflineMutation } from '@/hooks/useOfflineMutation';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import LabNavigation from '@/components/feature/LabNavigation';
import HistoriqueAnalyses from './components/HistoriqueAnalyses';

interface BonLaboratoire {
  id: string;
  patient_id: string;
  medecin_id: string;
  laborantin_id: string | null;
  sage_femme_id: string | null;
  prescripteur_type: string;
  type_analyse: string;
  description: string | null;
  urgences: boolean;
  observations_cliniques: string | null;
  date_prescription: string | null;
  statut: string;
  resultats: string | null;
  fichier_resultat_url: string | null;
  date_resultat: string | null;
  created_at: string;
  updated_at: string;
  patients?: {
    nom: string;
    prenom: string;
    telephone: string;
    email: string;
  } | null;
  medecins?: {
    nom: string;
    prenom: string;
    specialite: string;
  } | null;
  sage_femmes?: {
    nom: string;
    prenom: string;
  } | null;
  laborantins?: {
    nom: string;
    prenom: string;
  } | null;
}

type TabType = 'en_attente' | 'en_cours' | 'termine' | 'historique';

export default function LaborantinDashboardPage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('en_attente');
  const [bons, setBons] = useState<BonLaboratoire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal saisie des résultats
  const [selectedBon, setSelectedBon] = useState<BonLaboratoire | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultatsText, setResultatsText] = useState('');
  const [resultatsFileUrl, setResultatsFileUrl] = useState('');
  const [savingResults, setSavingResults] = useState(false);
  const [resultSuccess, setResultSuccess] = useState('');

  // Stats
  const [laborantinId, setLaborantinId] = useState('');
  const [stats, setStats] = useState({ en_attente: 0, en_cours: 0, termine: 0 });
  const { mutate: offlineMutate } = useOfflineMutation<any>();

  // Notification temps réel
  const [newBonAlert, setNewBonAlert] = useState<{ visible: boolean; patientName: string }>({ visible: false, patientName: '' });
  const newBonTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Son de notification
  const playNotificationSound = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') ctx.resume();

      const now = ctx.currentTime;
      const gainNode = ctx.createGain();
      gainNode.connect(ctx.destination);
      gainNode.gain.setValueAtTime(0.6, now);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 1.2);

      // Double "ding-ding" hospital-style chime
      const freqs = [880, 1100]; // A5, C#6 - pleasant chime
      freqs.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now + i * 0.3);
        osc.connect(gainNode);
        osc.start(now + i * 0.3);
        osc.stop(now + i * 0.3 + 0.25);
      });
    } catch {
      // Audio not available - silently ignore
    }
  }, []);

  // Realtime subscription
  useEffect(() => {
    if (!user) return;

    channelRef.current = supabase
      .channel('bons-laboratoire-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'bons_laboratoire', filter: 'statut=eq.en_attente' },
        async (payload) => {
          const newBon = payload.new as BonLaboratoire;
          // Ne pas traiter si déjà assigné
          if (newBon.laborantin_id) return;

          // Fetch full data including relations
          const { data: fullBon } = await supabase
            .from('bons_laboratoire')
            .select(`
              *,
              patients!inner(nom, prenom, telephone, email),
              medecins(nom, prenom, specialite),
              sage_femmes(nom, prenom)
            `)
            .eq('id', newBon.id)
            .single();

          if (fullBon) {
            const bonWithRelations = fullBon as BonLaboratoire;

            // Jouer le son
            playNotificationSound();

            // Ajouter au state
            setBons((prev) => {
              const exists = prev.some((b) => b.id === bonWithRelations.id);
              if (exists) return prev;
              return [bonWithRelations, ...prev];
            });

            setStats((prev) => ({
              ...prev,
              en_attente: prev.en_attente + 1,
            }));

            // Afficher l'alerte visuelle
            const patientName = `${bonWithRelations.patients?.nom || ''} ${bonWithRelations.patients?.prenom || ''}`;
            setNewBonAlert({ visible: true, patientName });

            if (newBonTimer.current) clearTimeout(newBonTimer.current);
            newBonTimer.current = setTimeout(() => {
              setNewBonAlert({ visible: false, patientName: '' });
            }, 8000);
          }
        }
      )
      .subscribe();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (newBonTimer.current) clearTimeout(newBonTimer.current);
    };
  }, [user, playNotificationSound]);

  // Nettoyer AudioContext au unmount
  useEffect(() => {
    return () => {
      if (audioCtxRef.current) {
        audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'laborantin' && role !== 'super_admin') { navigate('/', { replace: true }); return; }
    loadBons();
  }, [user, authLoading, role]);

  const loadBons = async () => {
    if (!user) return;
    setLoading(true);
    setError('');

    try {
      // Recuperer l'ID du laborantin connecte
      const { data: laboData } = await supabase
        .from('laborantins').select('id').eq('user_id', user.id).maybeSingle();
      const laboId = laboData?.id || '';
      setLaborantinId(laboId);

      // Bons non assignes (visibles par tous les laborantins)
      const { data: nonAssignes, error: err1 } = await supabase
        .from('bons_laboratoire')
        .select(`
          *,
          patients!inner(nom, prenom, telephone, email),
          medecins(nom, prenom, specialite),
          sage_femmes(nom, prenom)
        `)
        .is('laborantin_id', null)
        .eq('statut', 'en_attente')
        .order('urgences', { ascending: false })
        .order('created_at', { ascending: false });

      // Tous les bons en cours (visibles par tous les laborantins)
      const { data: enCours, error: err2 } = await supabase
        .from('bons_laboratoire')
        .select(`
          *,
          patients!inner(nom, prenom, telephone, email),
          medecins(nom, prenom, specialite),
          sage_femmes(nom, prenom),
          laborantins(nom, prenom)
        `)
        .eq('statut', 'en_cours')
        .order('urgences', { ascending: false })
        .order('created_at', { ascending: false });

      // Tous les bons termines (visibles par tous les laborantins)
      const { data: termines, error: err3 } = await supabase
        .from('bons_laboratoire')
        .select(`
          *,
          patients!inner(nom, prenom, telephone, email),
          medecins(nom, prenom, specialite),
          sage_femmes(nom, prenom)
        `)
        .eq('statut', 'termine')
        .order('created_at', { ascending: false });

      if (err1) throw err1;
      if (err2) throw err2;
      if (err3) throw err3;

      const combined = [...(nonAssignes || []), ...(enCours || []), ...(termines || [])] as BonLaboratoire[];
      const deduped = Array.from(new Map(combined.map((b) => [b.id, b])).values());
      setBons(deduped);

      const s = { en_attente: 0, en_cours: 0, termine: 0 };
      deduped.forEach((b: BonLaboratoire) => {
        if (b.laborantin_id === null && b.statut === 'en_attente') s.en_attente++;
        else if (b.statut === 'en_cours') s.en_cours++;
        else if (b.statut === 'termine') s.termine++;
      });
      setStats(s);
    } catch (err: any) {
      console.error('Erreur chargement bons:', err);
      setError(err.message || 'Erreur lors du chargement des bons');
    } finally {
      setLoading(false);
    }
  };

  const handleTakeInCharge = async (bon: BonLaboratoire) => {
    try {
      await offlineMutate({
        table: 'bons_laboratoire',
        operation: 'UPDATE',
        recordId: bon.id,
        data: {
          statut: 'en_cours',
          laborantin_id: laborantinId,
          updated_at: new Date().toISOString()
        },
        metadata: { summary: `Prise en charge bon labo — ${bon.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          const { error: updateError } = await supabase
            .from('bons_laboratoire')
            .update({
              statut: 'en_cours',
              laborantin_id: laborantinId,
              updated_at: new Date().toISOString()
            })
            .eq('id', bon.id);
          if (updateError) throw updateError;
          return { id: bon.id };
        },
      });

      setBons(prev =>
        prev.map(b => b.id === bon.id ? { ...b, statut: 'en_cours', laborantin_id: laborantinId } : b)
      );
      setStats(prev => ({
        ...prev,
        en_attente: prev.en_attente - 1,
        en_cours: prev.en_cours + 1,
      }));
    } catch (err: any) {
      console.error('Erreur prise en charge:', err);
      setError(err.message);
    }
  };

  const openResultModal = (bon: BonLaboratoire) => {
    setSelectedBon(bon);
    setResultatsText(bon.resultats || '');
    setResultatsFileUrl(bon.fichier_resultat_url || '');
    setResultSuccess('');
    setShowResultModal(true);
  };

  const handleSaveResults = async () => {
    if (!selectedBon) return;
    if (!resultatsText.trim()) {
      setError('Veuillez saisir les résultats d\'analyse');
      return;
    }

    setSavingResults(true);
    setError('');

    try {
      const updateData = {
        resultats: resultatsText.trim(),
        fichier_resultat_url: resultatsFileUrl.trim() || null,
        date_resultat: new Date().toISOString(),
        statut: 'termine',
        updated_at: new Date().toISOString(),
      };

      await offlineMutate({
        table: 'bons_laboratoire',
        operation: 'UPDATE',
        recordId: selectedBon.id,
        data: updateData,
        metadata: { summary: `Résultats labo — ${selectedBon.patients?.nom || 'Patient'}` },
        onlineExecutor: async () => {
          const { error: updateError } = await supabase
            .from('bons_laboratoire')
            .update(updateData)
            .eq('id', selectedBon.id);
          if (updateError) throw updateError;
          return { id: selectedBon.id };
        },
      });

      setBons(prev =>
        prev.map(b =>
          b.id === selectedBon.id
            ? {
                ...b,
                resultats: resultatsText.trim(),
                fichier_resultat_url: resultatsFileUrl.trim() || null,
                date_resultat: new Date().toISOString(),
                statut: 'termine',
              }
            : b
        )
      );
      setStats(prev => ({
        ...prev,
        en_cours: prev.en_cours - (selectedBon.statut === 'en_cours' ? 1 : 0),
        en_attente: prev.en_attente - (selectedBon.statut === 'en_attente' ? 1 : 0),
        termine: prev.termine + 1,
      }));

      setResultSuccess('Résultats enregistrés avec succès !');
      setTimeout(() => {
        setShowResultModal(false);
        setSelectedBon(null);
      }, 1500);
    } catch (err: any) {
      console.error('Erreur sauvegarde résultats:', err);
      setError(err.message);
    } finally {
      setSavingResults(false);
    }
  };

  const filteredBons = bons.filter(bon => {
    if (activeTab === 'en_attente') {
      return bon.laborantin_id === null && bon.statut === 'en_attente';
    }
    if (activeTab === 'en_cours') {
      return bon.statut === 'en_cours';
    }
    if (activeTab === 'termine') {
      return bon.statut === 'termine';
    }
    return false;
  });

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case 'en_attente':
        return (
          <span className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-xs font-semibold">
            En attente
          </span>
        );
      case 'en_cours':
        return (
          <span className="px-3 py-1.5 bg-clinic-blue/10 text-clinic-blue-dark rounded-full text-xs font-semibold">
            En cours
          </span>
        );
      case 'termine':
        return (
          <span className="px-3 py-1.5 bg-green-50 text-green-700 rounded-full text-xs font-semibold">
            Terminé
          </span>
        );
      default:
        return (
          <span className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full text-xs font-semibold">
            {statut}
          </span>
        );
    }
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatDateTime = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-clinic-blue animate-spin mb-4 block"></i>
          <span className="text-clinic-blue-dark text-xl">Chargement des bons de laboratoire...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">

          {/* En-tête */}
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col gap-3 mb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-xl md:text-3xl font-bold text-clinic-blue-dark">Espace Biologiste médical</h1>
                  <p className="text-gray-600 text-xs md:text-sm">Gestion des analyses et bons de laboratoire</p>
                </div>
              </div>
            </div>
            <LabNavigation />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            <div className="bg-white rounded-xl border border-gray-100 px-5 py-4">
              <p className="text-2xl font-bold text-clinic-blue-dark md:text-3xl">{stats.en_attente}</p>
              <p className="mt-1 text-xs text-gray-500">En attente</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 px-5 py-4">
              <p className="text-2xl font-bold text-clinic-blue-dark md:text-3xl">{stats.en_cours}</p>
              <p className="mt-1 text-xs text-gray-500">En cours</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-100 px-5 py-4">
              <p className="text-2xl font-bold text-clinic-blue-dark md:text-3xl">{stats.termine}</p>
              <p className="mt-1 text-xs text-gray-500">Terminés</p>
            </div>
          </div>

          {/* Onglets */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 md:gap-4 mb-5 md:mb-6">
            <div className="overflow-x-auto">
            <div className="flex gap-1 bg-gray-100 p-1 rounded-full w-fit min-w-max">
              {([
                { key: 'en_attente', label: 'En attente', icon: 'ri-time-line', count: stats.en_attente as number | null },
                { key: 'en_cours', label: 'En cours', icon: 'ri-loader-4-line', count: stats.en_cours as number | null },
                { key: 'termine', label: 'Terminés', icon: 'ri-check-double-line', count: stats.termine as number | null },
                { key: 'historique', label: 'Historique', icon: 'ri-history-line', count: null },
              ] as { key: TabType; label: string; icon: string; count: number | null }[]).map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer transition-all flex items-center gap-2 ${
                    activeTab === tab.key
                      ? 'bg-white text-clinic-blue-dark shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                  {tab.count !== null && (
                    <span className={`ml-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      activeTab === tab.key
                        ? 'bg-clinic-blue/10 text-clinic-blue'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
              <i className="ri-error-warning-line text-red-500 text-xl"></i>
              <p className="text-red-700 text-sm flex-1">{error}</p>
              <button onClick={() => setError('')} className="text-red-400 hover:text-red-600 cursor-pointer">
                <i className="ri-close-line"></i>
              </button>
            </div>
          )}

          {/* Alerte nouveau bon en temps réel */}
          {newBonAlert.visible && (
            <div className="mb-6 p-5 bg-clinic-blue/5 border border-clinic-blue/30 rounded-xl flex items-center gap-4 animate-pulse">
              <div className="flex-1">
                <p className="text-lg font-bold text-clinic-blue-dark">Nouveau bon de laboratoire !</p>
                <p className="text-sm text-clinic-blue-dark">
                  Un nouveau bon vient d'arriver pour le patient <strong>{newBonAlert.patientName}</strong>
                </p>
              </div>
              <button
                onClick={() => { setNewBonAlert({ visible: false, patientName: '' }); setActiveTab('en_attente'); }}
                className="px-5 py-2.5 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
              >
                Voir
              </button>
            </div>
          )}

          {/* Liste des bons ou historique */}
          {activeTab === 'historique' ? (
            <HistoriqueAnalyses />
          ) : filteredBons.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Aucun bon trouvé</h3>
              <p className="text-gray-500">
                {activeTab === 'en_attente'
                  ? 'Tous les bons ont été traités.'
                  : activeTab === 'en_cours'
                  ? 'Aucune analyse en cours.'
                  : 'Aucune analyse terminée pour le moment.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBons.map((bon) => (
                <div
                  key={bon.id}
                  className={`bg-white rounded-2xl border-2 p-5 md:p-6 transition-all ${
                    bon.urgences ? 'border-red-300 bg-red-50/30' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Infos patient + médecin */}
                    <div className="flex-1 space-y-3">
                      {/* Patient */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-clinic-blue-dark">
                          {bon.patients?.nom} {bon.patients?.prenom}
                        </p>
                        <p className="text-xs text-gray-500">
                          {bon.patients?.telephone || bon.patients?.email || '—'}
                        </p>
                        {bon.urgences && (
                          <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                            URGENT
                          </span>
                        )}
                      </div>

                      {/* Prescripteur */}
                      <div className="text-sm text-gray-600">
                        {bon.prescripteur_type === 'sage_femme' ? (
                          <>Prescrit par <strong>Sage-femme {bon.sage_femmes?.prenom} {bon.sage_femmes?.nom}</strong></>
                        ) : (
                          <>Prescrit par <strong>Dr {bon.medecins?.nom} {bon.medecins?.prenom}</strong>
                          {bon.medecins?.specialite && ` — ${bon.medecins.specialite}`}</>
                        )}
                      </div>

                      {/* Type d'analyse */}
                      <div className="text-sm font-semibold text-clinic-blue-dark">{bon.type_analyse}</div>
                      {bon.description && (
                        <p className="text-sm text-gray-600">{bon.description}</p>
                      )}

                      {/* Observations cliniques */}
                      {bon.observations_cliniques && (
                        <p className="text-sm text-gray-500 italic">{bon.observations_cliniques}</p>
                      )}

                      {/* Dates */}
                      <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                        <span>Prescrit le {formatDate(bon.date_prescription)}</span>
                        {bon.date_resultat && (
                          <span>Résultats le {formatDateTime(bon.date_resultat)}</span>
                        )}
                      </div>

                      {/* Résultats (si terminé) */}
                      {bon.statut === 'termine' && bon.resultats && (
                        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-2">
                          <p className="text-sm font-semibold text-green-800 mb-1">
                            <i className="ri-check-double-line mr-1"></i>Résultats
                          </p>
                          <p className="text-sm text-gray-700 whitespace-pre-line">{bon.resultats}</p>
                          {bon.fichier_resultat_url && (
                            <a
                              href={bon.fichier_resultat_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 mt-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
                            >
                              <i className="ri-file-pdf-line"></i>
                              Voir le fichier joint
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row lg:flex-col items-center lg:items-end gap-2 lg:gap-3 flex-shrink-0">
                      {getStatutBadge(bon.statut)}

                      {bon.statut === 'en_attente' && (
                        <button
                          onClick={() => handleTakeInCharge(bon)}
                          className="px-4 py-2.5 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                        >
                          Prendre en charge
                        </button>
                      )}

                      {bon.statut === 'en_cours' && (
                        bon.laborantin_id === laborantinId ? (
                          <button
                            onClick={() => navigate(`/laborantin/saisir-analyses?bonId=${bon.id}`)}
                            className="px-4 py-2.5 bg-clinic-blue text-white rounded-full hover:bg-clinic-blue-dark transition-all font-semibold text-sm whitespace-nowrap cursor-pointer"
                          >
                            Saisir résultats
                          </button>
                        ) : (
                          <span className="px-3 py-2 bg-clinic-blue/5 text-clinic-blue-dark rounded-full text-xs font-semibold whitespace-nowrap text-center">
                            Traité par {bon.laborantins?.prenom} {bon.laborantins?.nom}
                          </span>
                        )
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <Footer />

      {/* Modal saisie des résultats */}
      {showResultModal && selectedBon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 md:p-8 max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-clinic-blue-dark flex items-center gap-2">
                <i className="ri-microscope-line text-teal-600"></i>
                Saisie des résultats
              </h3>
              <button
                onClick={() => { setShowResultModal(false); setSelectedBon(null); setError(''); }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            {/* Rappel des infos du bon */}
            <div className="bg-gray-50 rounded-2xl p-4 mb-6">
              <div className="flex flex-wrap gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Patient :</span>{' '}
                  <strong>{selectedBon.patients?.nom} {selectedBon.patients?.prenom}</strong>
                </div>
                <div>
                  <span className="text-gray-500">Analyse :</span>{' '}
                  <strong>{selectedBon.type_analyse}</strong>
                </div>
                <div>
                  <span className="text-gray-500">Prescrit le :</span>{' '}
                  <strong>{formatDate(selectedBon.date_prescription)}</strong>
                </div>
              </div>
              {selectedBon.urgences && (
                <div className="mt-2">
                  <span className="px-2.5 py-1 bg-red-100 text-red-700 rounded-full text-xs font-bold">
                    <i className="ri-alert-fill mr-1"></i>URGENT
                  </span>
                </div>
              )}
            </div>

            {/* Succès */}
            {resultSuccess && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-green-600 text-xl"></i>
                <span className="text-green-700 text-sm">{resultSuccess}</span>
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
                <i className="ri-error-warning-line text-red-500 text-xl"></i>
                <span className="text-red-700 text-sm">{error}</span>
                <button onClick={() => setError('')} className="ml-auto text-red-400 cursor-pointer">
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}

            <div className="space-y-5">
              {/* Résultats */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Résultats d'analyse <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={resultatsText}
                  onChange={(e) => setResultatsText(e.target.value)}
                  rows={6}
                  maxLength={500}
                  placeholder="Saisissez les résultats détaillés de l'analyse..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all resize-none"
                ></textarea>
                <p className="text-xs text-gray-400 mt-1 text-right">{resultatsText.length}/500 caractères</p>
              </div>

              {/* URL fichier joint */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Lien vers le fichier de résultats (optionnel)
                </label>
                <input
                  type="url"
                  value={resultatsFileUrl}
                  onChange={(e) => setResultatsFileUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all"
                />
              </div>

              {/* Boutons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowResultModal(false); setSelectedBon(null); setError(''); }}
                  className="px-5 py-3 border border-gray-300 text-gray-600 rounded-full hover:bg-gray-50 transition-all text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSaveResults}
                  disabled={savingResults || resultSuccess !== ''}
                  className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-all font-semibold text-sm whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {savingResults ? (
                    <><i className="ri-loader-4-line animate-spin mr-2"></i>Enregistrement...</>
                  ) : (
                    <><i className="ri-check-line mr-2"></i>Enregistrer les résultats</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}